import React, { FC, ReactNode, createContext, useState } from 'react';
import { Notification, NotificationProps } from '@components/molecules/Notification';
import { NOTIFICATIONS_DISMISS_SECONDS } from '@constants';

type NotificationWithId = NotificationProps & { id: string };

interface ShowDefaultNotificationProps {
    message: NotificationProps['message'];
    type: NotificationProps['type'];
    autoDismiss?: NotificationProps['autoDismiss'];
    children?: NotificationProps['children'];
}

type NotificationContext = {
    showDefaultNotification: (props: ShowDefaultNotificationProps) => void;
    clearNotifications: () => void;
};

type NotificationContextProvider = {
    children: ReactNode;
};

const MIN_ID = 1;
const MAX_ID = 99999;

export const NotificationsContext = createContext<NotificationContext>({
    showDefaultNotification: () => {},
    clearNotifications: () => {},
});

export const NotificationsProvider: FC<NotificationContextProvider> = ({ children }) => {
    const [notifications, setNotifications] = useState<NotificationWithId[]>([]);

    const addNotification = (notification: NotificationWithId) => {
        setNotifications((notifications) => [...notifications, notification]);
    };

    const removeNotification = (id: string) => {
        setNotifications((notifications) => [...notifications.filter((item) => item.id !== id)]);
    };

    const getRandomId = () => `${Math.floor(Math.random() * (MAX_ID - MIN_ID + 1)) + MIN_ID}`;

    const contextValue: NotificationContext = {
        showDefaultNotification: (props) => {
            const notification: NotificationWithId = {
                id: getRandomId(),
                size: 'sm',
                position: { bottom: '20px', align: 'center' },
                dismissible: true,
                autoDismiss: NOTIFICATIONS_DISMISS_SECONDS,
                borderRadiusStyle: 'rounded',
                children: <></>,
                ...props,
            };
            addNotification(notification);
        },
        clearNotifications: () => setNotifications([]),
    };

    return (
        <NotificationsContext.Provider value={contextValue}>
            <div>
                <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 1056 }}>
                    {notifications.map((notification, index) => (
                        <Notification
                            key={`notification-${notification.id}`}
                            {...notification}
                            position={{ bottom: `${20 + index * 70}px`, align: 'center' }}
                            onClose={() => removeNotification(notification.id)}
                        />
                    ))}
                </div>

                {children}
            </div>
        </NotificationsContext.Provider>
    );
};
